import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  useTree,
  List,
  ThemeIcon,
  SegmentedControl,
  Menu,
  Tooltip,
} from "@mantine/core"
import { useField } from "@mantine/form"
import { useDebouncedValue, useHotkeys } from "@mantine/hooks"
import { openConfirmModal } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import {
  IconAlertTriangle,
  IconCursorText,
  IconDeviceFloppy,
  IconFolderPlus,
  IconSearch,
  IconTrash,
  IconX,
  IconPlus,
  IconLayoutDashboard,
  IconEye,
} from "@tabler/icons-react"
import _ from "lodash"
import React, { FC, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"

import { TreeNodeType } from "@costory/types/endpoints/folders"
import { ComponentType } from "@costory/types/prisma-client"
import { User } from "@costory/types/user"

import { ROOT_FOLDER_ID } from "@costory/shared/const"

import { DefaultFileLeaf } from "@costory/front/components/FileExplorer/DefaultFileLeaf"
import { DefaultFolderNode } from "@costory/front/components/FileExplorer/DefaultFolderNode"
import { FileExplorerHeader } from "@costory/front/components/FileExplorer/FileExplorerHeader"
import { SearchExplorer } from "@costory/front/components/FileExplorer/FoldersSearch"
import {
  DashboardLeafProps,
  SavedViewLeafProps,
  TreeExplorer,
} from "@costory/front/components/FileExplorer/TreeExplorer"
import { runtimeConfig } from "@costory/front/lib/runtimeConfig"
import { useAuthState } from "@costory/front/queries/auth"
import {
  useDeleteDashboard,
  useRenameDashboard,
  useDashboardQuery,
} from "@costory/front/queries/dashboard"
import {
  useCreateFolder,
  useDeleteFolder,
  useRenameFolder,
} from "@costory/front/queries/folders"
import {
  useDeleteSavedView,
  useRenameSavedView,
  useSavedViewUsage,
} from "@costory/front/queries/savedViews"

const COSTORY_ORGID = runtimeConfig.VITE_COSTORY_ORGID
const COSTORY_EMAIL_DOMAIN = runtimeConfig.VITE_COSTORY_EMAIL_DOMAIN

const isAllowedToEdit = (
  node: {
    id: string
    label: string
    type: TreeNodeType
    closestFolderId: string | null
    ownerId: string
    componentType?: ComponentType
  },
  user?: User,
) => {
  if (node.id == "cos_ROOT") {
    return false
  }
  if (node.componentType === ComponentType.COMMUNITY) {
    return (
      COSTORY_ORGID === user?.currentOrg.id &&
      user?.email.endsWith(COSTORY_EMAIL_DOMAIN) &&
      user?.isAdmin
    )
  }
  return node.ownerId === user?.id || user?.isAdmin
}

interface FileExplorerProps {
  onOpenDashboard?: (id: string, newWindow?: boolean) => void
  onOpenSavedView?: (id: string, newWindow?: boolean) => void
  onOpenFolder?: (id: string) => void
  onDoubleClick: (
    id: string,
    type: TreeNodeType,
    event?: React.MouseEvent,
  ) => void
  buttonProps?: {
    label?: string
    disabled?: boolean
    loading?: boolean
    disabledTooltip?: string
  }
  defaultShouldShowSavedViews?: boolean
  defaultShouldShowDashboards?: boolean
  optionNewDashboardButton?: boolean
  disableHighlightStyle?: boolean
  enableFileActions?: boolean
  isInSaveMode: boolean
}

export const FileExplorer: FC<FileExplorerProps> = ({
  onOpenDashboard,
  onOpenSavedView,
  onOpenFolder,
  onDoubleClick,
  optionNewDashboardButton,
  defaultShouldShowDashboards = true,
  defaultShouldShowSavedViews = true,
  buttonProps: { label, disabled, loading, disabledTooltip } = {
    label: undefined,
    disabled: false,
    loading: false,
    disabledTooltip: undefined,
  },
  disableHighlightStyle = false,
  enableFileActions = false,
  isInSaveMode,
}) => {
  const { user } = useAuthState()
  const [whatToShow, setWhatToShow] = useState<
    "savedViews" | "all" | "dashboards"
  >(
    defaultShouldShowSavedViews && defaultShouldShowDashboards
      ? "all"
      : defaultShouldShowSavedViews
        ? "savedViews"
        : defaultShouldShowDashboards
          ? "dashboards"
          : "all",
  )
  const tree = useTree({
    initialExpandedState: { [ROOT_FOLDER_ID]: true },
  })
  const [search, setSearch] = useState("")
  const searchField = useField({ initialValue: search })
  const nameField = useField({ initialValue: "", mode: "uncontrolled" })
  const folderNameField = useField({ initialValue: "", mode: "uncontrolled" })
  const [debouncedSearch] = useDebouncedValue(searchField.getValue(), 300)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [keyboardFocusIndex, setKeyboardFocusIndex] = useState<number>(-1)

  useHotkeys([
    ["mod+K", () => searchInputRef.current?.focus()],
    [
      "Enter",
      (e) => {
        if (document.activeElement === searchInputRef.current) return
        if (selectedNode) {
          e.preventDefault()
          if (selectedNode.type === TreeNodeType.Folder) {
            tree.expand(selectedNode.id)
          } else {
            handleClickOpenSelectedFile()
          }
        }
      },
    ],
  ])
  const [selectedNode, setSelectedNode] = useState<{
    id: string
    label: string
    type: TreeNodeType
    closestFolderId: string | null
    ownerId: string
    componentType?: ComponentType
  } | null>(null)

  const { data: dashboardQueryData } = useDashboardQuery(
    selectedNode?.type === TreeNodeType.Dashboard ? selectedNode.id : "",
    selectedNode?.type === TreeNodeType.Dashboard,
  )
  const dashboardDetails = dashboardQueryData?.dashboard

  const { refetch: refetchSavedViewUsage } = useSavedViewUsage(
    selectedNode?.type === TreeNodeType.SavedView ? selectedNode.id : undefined,
  )

  useEffect(() => {
    setSearch(debouncedSearch)
    setKeyboardFocusIndex(-1) // Reset keyboard focus when search changes
  }, [debouncedSearch])

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setKeyboardFocusIndex((prev) => prev + 1)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setKeyboardFocusIndex((prev) => Math.max(-1, prev - 1))
    } else if (e.key === "Enter" && keyboardFocusIndex >= 0) {
      e.preventDefault()
      if (selectedNode) {
        if (selectedNode.type === TreeNodeType.Folder) {
          tree.expand(selectedNode.id)
        } else {
          handleClickOpenSelectedFile()
        }
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      searchInputRef.current?.blur()
    }
  }

  const {
    mutateAsync: deleteDashboard,
    isPending: isDeleteDashboardMutationPending,
  } = useDeleteDashboard()
  const {
    mutateAsync: deleteSavedView,
    isPending: isDeleteSavedViewMutationPending,
  } = useDeleteSavedView()
  const {
    mutateAsync: deleteFolder,
    isPending: isDeleteFolderMutationPending,
  } = useDeleteFolder()

  const {
    mutateAsync: renameDashboard,
    isPending: isRenameDashboardMutationPending,
  } = useRenameDashboard()
  const {
    mutateAsync: renameSavedView,
    isPending: isRenameSavedViewMutationPending,
  } = useRenameSavedView()
  const {
    mutateAsync: renameFolder,
    isPending: isRenameFolderMutationPending,
  } = useRenameFolder()

  const {
    mutateAsync: createFolder,
    isPending: isCreateFolderMutationPending,
  } = useCreateFolder()

  const handleSelect = (
    id: string,
    label: string,
    type: TreeNodeType,
    closestFolderId: string | null,
    ownerId: string,
    componentType?: ComponentType,
  ) => {
    if (selectedNode?.id === id) {
      setSelectedNode(null)
      return
    }
    setSelectedNode({
      id,
      label,
      type,
      closestFolderId,
      ownerId,
      componentType,
    })
  }

  const handleClickOpenSelectedFile = () => {
    if (selectedNode?.type === TreeNodeType.Dashboard) {
      onOpenDashboard?.(selectedNode.id)
    }
    if (selectedNode?.type === TreeNodeType.SavedView) {
      onOpenSavedView?.(selectedNode.id)
    }
    if (selectedNode?.type === TreeNodeType.Folder) {
      onOpenFolder?.(selectedNode.id)
    }
  }

  const handleClickDeleteSelectedFile = async () => {
    if (!selectedNode) return

    const { type, label, id } = selectedNode

    const showBlockDeleteModal = (title: string, message: React.ReactNode) => {
      openConfirmModal({
        title: (
          <Group wrap="nowrap">
            <ThemeIcon size={27} variant="light" color="warning">
              <IconAlertTriangle size={20} />
            </ThemeIcon>
            <Title order={3}>{title}</Title>
          </Group>
        ),
        children: <Text>{message}</Text>,
        labels: { confirm: "OK", cancel: "Cancel" },
        confirmProps: { style: { display: "none" } },
      })
    }

    if (
      type === TreeNodeType.Dashboard &&
      dashboardDetails &&
      (dashboardDetails.isDefaultDashboard ||
        dashboardDetails.isOrgDefaultDashboard)
    ) {
      showBlockDeleteModal(
        "Cannot delete default dashboard",
        <React.Fragment>
          This dashboard is set as your default dashboard
          {dashboardDetails.isOrgDefaultDashboard
            ? " for your organization"
            : ""}
          .
          <br />
          Cannot delete the dashboard until another is set as default.
        </React.Fragment>,
      )
      return
    }

    if (type === TreeNodeType.SavedView) {
      const usage = await refetchSavedViewUsage()
      const dashboards = usage.data?.dashboards || []

      if (usage.data?.used) {
        showBlockDeleteModal(
          "Cannot delete view",
          <React.Fragment>
            This view is currently used in the following dashboard
            {dashboards.length > 1 ? "s" : ""}:
            <List spacing="xs" mt={8}>
              {dashboards.map((d: { id: string; name: string }) => (
                <List.Item key={d.id}>
                  <Text fw={500}>{d.name}</Text>
                </List.Item>
              ))}
            </List>
            <br />
            Cannot delete the view until it is removed from all dashboards.
          </React.Fragment>,
        )
        return
      }
    }

    openConfirmModal({
      title: <Title>Are you sure?</Title>,
      children: (
        <Text>
          Do you really want to delete{" "}
          {type === TreeNodeType.Dashboard ? "dashboard" : "view"}{" "}
          <Text span fw={600} fs="italic">
            {label}
          </Text>
          ?
        </Text>
      ),
      confirmProps: {
        color: "red",
        loading:
          isDeleteDashboardMutationPending ||
          isDeleteSavedViewMutationPending ||
          isDeleteFolderMutationPending,
      },
      labels: { confirm: "Delete", cancel: "Cancel" },
      onConfirm: async () => {
        try {
          if (type === TreeNodeType.Dashboard) {
            await deleteDashboard(id)
          } else if (type === TreeNodeType.SavedView) {
            await deleteSavedView(id)
          } else if (type === TreeNodeType.Folder) {
            await deleteFolder(id)
          }

          showNotification({
            title: "Success",
            message: `${type} successfully deleted`,
            color: "green",
          })
          setSelectedNode(null)
        } catch {
          showNotification({
            title: "Error",
            message: "An error occurred while deleting the file",
            color: "red",
          })
        }
      },
    })
  }

  const handleClickCreateFolder = () => {
    // If a folder is selected, expand it to show the new subfolder
    if (selectedNode?.type === TreeNodeType.Folder) {
      tree.expand(selectedNode.closestFolderId!)
    }

    folderNameField.setValue("")

    openConfirmModal({
      title: <Title>Create folder</Title>,
      children: (
        <TextInput
          {...folderNameField.getInputProps()}
          placeholder="Enter folder name"
        />
      ),
      confirmProps: {
        loading: isCreateFolderMutationPending,
      },
      labels: {
        confirm: "Create",
        cancel: "Cancel",
      },
      onConfirm: async () => {
        const name = folderNameField.getValue()
        if (!name) {
          return
        }
        try {
          const createdFolder = await createFolder({
            name,
            // If no node selected, create at root; otherwise create as child
            parentFolderId: selectedNode?.closestFolderId ?? ROOT_FOLDER_ID,
          })
          tree.select(createdFolder.id)
          setSelectedNode({
            id: createdFolder.id,
            label: createdFolder.name,
            type: TreeNodeType.Folder,
            closestFolderId: createdFolder.parentFolderId,
            ownerId: createdFolder.createdById,
          })
          showNotification({
            title: "Success",
            message: "Folder successfully created",
            color: "green",
          })
        } catch {
          showNotification({
            title: "Error",
            message: "An error occured while creating the folder",
            color: "red",
          })
        }
      },
    })
  }

  const handleClickRenameSelectedNode = () => {
    if (!selectedNode) {
      return
    }

    nameField.setValue(selectedNode.label)

    openConfirmModal({
      title: <Title>Rename {selectedNode.type}</Title>,
      children: (
        <TextInput
          {...nameField.getInputProps()}
          placeholder="Enter new name"
        />
      ),
      confirmProps: {
        loading:
          isRenameDashboardMutationPending ||
          isRenameSavedViewMutationPending ||
          isRenameFolderMutationPending,
      },
      labels: {
        confirm: "Rename",
        cancel: "Cancel",
      },
      onConfirm: async () => {
        if (!selectedNode) {
          return
        }
        try {
          if (selectedNode.type === TreeNodeType.Dashboard) {
            await renameDashboard({
              id: selectedNode.id,
              name: nameField.getValue(),
            })
          }
          if (selectedNode.type === TreeNodeType.SavedView) {
            await renameSavedView({
              id: selectedNode.id,
              name: nameField.getValue(),
            })
          }
          if (selectedNode.type === TreeNodeType.Folder) {
            await renameFolder({
              id: selectedNode.id,
              name: nameField.getValue(),
            })
          }
          showNotification({
            title: "Success",
            message: `${selectedNode.type} successfully renamed`,
            color: "green",
          })
        } catch {
          showNotification({
            title: "Error",
            message: "An error occured while renaming the file",
            color: "red",
          })
        }
      },
    })
  }

  const renderLeaf = ({
    show,
    ...payload
  }: (DashboardLeafProps | SavedViewLeafProps) & { show: boolean }) => {
    const handleNodeDoubleClick = (e: React.MouseEvent) => {
      onDoubleClick(payload.node.value, payload.node.nodeProps.type, e)
    }
    const clonedProps = _.cloneDeep(payload)
    _.set(clonedProps, "node.nodeProps.onSelect", (e: React.MouseEvent) => {
      const {
        value,
        label,
        nodeProps: { type, parentFolderId, ownerId, componentType },
      } = clonedProps.node
      if (e.metaKey || e.ctrlKey) {
        onDoubleClick(value, type, e)
        return
      }
      handleSelect(
        value,
        label as string,
        type,
        parentFolderId,
        ownerId,
        componentType,
      )
      tree.toggleSelected(payload.node.value)
    })
    const props = _.set(
      clonedProps,
      "node.nodeProps.onDoubleClick",
      handleNodeDoubleClick,
    )

    // Disable views and dashboards when in save mode (when label prop exists)
    const isDisabled = isInSaveMode

    return show ? (
      <DefaultFileLeaf
        {...props}
        disableHighlightStyle={disableHighlightStyle}
        disabled={isDisabled}
      />
    ) : null
  }
  const shouldShowSavedViews =
    whatToShow === "savedViews" || whatToShow === "all"
  const shouldShowDashboards =
    whatToShow === "dashboards" || whatToShow === "all"

  return (
    <Stack h="90%">
      <Group justify="space-between" wrap="nowrap" w="100%" pos="relative">
        <SegmentedControl
          value={whatToShow}
          onChange={(value) =>
            setWhatToShow(value as "savedViews" | "dashboards" | "all")
          }
          data={[
            {
              label: <Text size="sm">All</Text>,
              value: "all",
            },
            {
              label: <Text size="sm">Views</Text>,
              value: "savedViews",
            },
            {
              label: <Text size="sm">Dashboards</Text>,
              value: "dashboards",
            },
          ]}
        />
        {!label && (
          <TextInput
            ref={searchInputRef}
            leftSection={<IconSearch />}
            placeholder="Search by name"
            {...searchField.getInputProps()}
            onKeyDown={handleSearchKeyDown}
            rightSection={
              search ? (
                <ActionIcon
                  onClick={() => {
                    searchField.setValue("")
                    setSelectedNode(null)
                  }}
                >
                  <IconX />
                </ActionIcon>
              ) : (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{
                    border: "1px solid var(--mantine-color-gray-4)",
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontWeight: 500,
                  }}
                >
                  {navigator.userAgent.toLowerCase().includes("mac")
                    ? "⌘K"
                    : "Ctrl+K"}
                </Text>
              )
            }
            pos="absolute"
            left="calc(50% - 200px)"
            w={400}
            styles={{
              input: {
                border: "none",
                borderBottom: "1px solid var(--mantine-color-gray-4)",
                backgroundColor: "transparent",
                borderRadius: 0,
              },
            }}
          />
        )}
        <Group>
          {selectedNode && enableFileActions && (
            <Group gap={4}>
              <Button
                variant="transparent"
                c={!isAllowedToEdit(selectedNode, user) ? "dimmed" : "gray"}
                leftSection={<IconCursorText />}
                disabled={!isAllowedToEdit(selectedNode, user)}
                onClick={handleClickRenameSelectedNode}
                loading={
                  isRenameFolderMutationPending ||
                  isRenameDashboardMutationPending ||
                  isRenameSavedViewMutationPending
                }
              >
                Rename
              </Button>
              <Button
                variant="transparent"
                c={!isAllowedToEdit(selectedNode, user) ? "dimmed" : "red"}
                leftSection={<IconTrash />}
                disabled={!isAllowedToEdit(selectedNode, user)}
                onClick={handleClickDeleteSelectedFile}
                loading={
                  isDeleteDashboardMutationPending ||
                  isDeleteSavedViewMutationPending ||
                  isDeleteFolderMutationPending
                }
              >
                Delete
              </Button>
            </Group>
          )}
          {selectedNode &&
          (selectedNode.type === TreeNodeType.Dashboard ||
            selectedNode.type === TreeNodeType.SavedView) ? (
            <Button
              disabled={
                disabled ||
                (selectedNode.type === TreeNodeType.Dashboard &&
                  !onOpenDashboard) ||
                (selectedNode.type === TreeNodeType.SavedView &&
                  !onOpenSavedView)
              }
              onClick={handleClickOpenSelectedFile}
            >
              Open
            </Button>
          ) : selectedNode &&
            selectedNode.type === TreeNodeType.Folder &&
            label ? (
            <Tooltip
              label={disabled && disabledTooltip ? disabledTooltip : undefined}
              disabled={!disabled || !disabledTooltip}
            >
              <Button
                disabled={disabled}
                loading={loading}
                leftSection={<IconDeviceFloppy />}
                onClick={handleClickOpenSelectedFile}
              >
                {label}
              </Button>
            </Tooltip>
          ) : (
            optionNewDashboardButton && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button variant="outline" leftSection={<IconPlus />}>
                    Add New
                  </Button>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconFolderPlus size={16} />}
                    onClick={handleClickCreateFolder}
                  >
                    New Folder
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconLayoutDashboard size={16} />}
                    component={Link}
                    to="/dashboards/new"
                  >
                    New Dashboard
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconEye size={16} />}
                    component={Link}
                    to="/explorer"
                  >
                    New View
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )
          )}
        </Group>
      </Group>
      <Stack h="90%">
        <Stack
          h="100%"
          styles={{
            root: {
              borderStyle: "solid",
              borderWidth: 1,
              borderColor: "var(--mantine-color-border-0)",
              borderRadius: 8,
              overflow: "auto",
            },
          }}
        >
          <FileExplorerHeader />
          {search && !label ? (
            <SearchExplorer
              search={search}
              onSelect={(
                id,
                label,
                type,
                closestFolderId,
                ownerId,
                componentType,
              ) =>
                setSelectedNode({
                  id,
                  type,
                  label,
                  closestFolderId,
                  ownerId,
                  componentType,
                })
              }
              shouldShowSavedViews={
                whatToShow === "savedViews" || whatToShow === "all"
              }
              shouldShowDashboards={shouldShowDashboards}
              onDoubleClick={onDoubleClick}
              disableHighlightStyle={disableHighlightStyle}
              keyboardFocusIndex={keyboardFocusIndex}
            />
          ) : (
            <TreeExplorer
              tree={tree}
              renderSavedViewLeaf={(payload) =>
                renderLeaf({
                  ...payload,
                  show: shouldShowSavedViews,
                })
              }
              renderDashboardLeaf={(payload) =>
                renderLeaf({
                  ...payload,
                  show: shouldShowDashboards,
                })
              }
              renderFolderNode={(payload) => {
                const clonedProps = _.cloneDeep(payload)
                _.set(clonedProps, "node.nodeProps.onSelect", () => {
                  const {
                    value,
                    label,
                    nodeProps: { type, ownerId },
                  } = clonedProps.node
                  const hasCommunityComponent = _.find(
                    clonedProps.node.children,
                    {
                      nodeProps: { componentType: ComponentType.COMMUNITY },
                    },
                  )
                  const componentType = hasCommunityComponent
                    ? ComponentType.COMMUNITY
                    : undefined

                  handleSelect(
                    value,
                    label as string,
                    type,
                    value,
                    ownerId,
                    componentType,
                  )
                  tree.toggleSelected(clonedProps.node.value)
                })
                const props = _.set(
                  clonedProps,
                  "node.nodeProps.onDoubleClick",
                  () => {
                    onDoubleClick(
                      payload.node.value,
                      payload.node.nodeProps.type,
                    )
                  },
                )

                return <DefaultFolderNode {...props} />
              }}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
